import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScheduledProgram } from '../../hooks/useSchedules';
import { useModalKeyTrap } from '../../hooks/useModalKeyTrap';
import './ScheduleReminderModal.css';

interface ScheduleReminderModalProps {
  schedule: ScheduledProgram;
  onConfirm: () => void;
  onCancel: () => void;
  /** 'watch' (default) shows Assistir/Cancel and navigates to live TV.
   *  'info' shows a single OK button — purely informational (e.g. sports). */
  mode?: 'watch' | 'info';
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

/** Returns a "starts in X min" or "started Xm ago" label */
function getTimingLabel(startMs: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diffMs = startMs - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin > 0) return t('scheduleReminder.startsIn', { count: diffMin });
  if (diffMin === 0) return t('scheduleReminder.startingNow');
  return t('scheduleReminder.startedAgo', { count: Math.abs(diffMin) });
}

const ScheduleReminderModal: React.FC<ScheduleReminderModalProps> = ({
  schedule,
  onConfirm,
  onCancel,
  mode = 'watch',
}) => {
  const { t } = useTranslation();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const [timingLabel, setTimingLabel] = useState(() => getTimingLabel(schedule.startMs, t));

  // Update "starts in X min" every 30s
  useEffect(() => {
    setTimingLabel(getTimingLabel(schedule.startMs, t));
    const id = setInterval(
      () => setTimingLabel(getTimingLabel(schedule.startMs, t)),
      30_000,
    );
    return () => clearInterval(id);
  }, [schedule.startMs, t]);

  // Keyboard: Enter/ArrowLeft → confirm, ArrowRight/Back → cancel
  useModalKeyTrap(useCallback(function (key, e) {
    if (key === 'Enter' || key === 'Space') {
      e.preventDefault();
      if (mode === 'info') { onCancel(); return; }
      if (document.activeElement === confirmRef.current) {
        onConfirm();
      } else {
        onCancel();
      }
    } else if (key === 'ArrowLeft') {
      e.preventDefault();
      if (mode !== 'info') confirmRef.current?.focus();
    } else if (key === 'ArrowRight') {
      e.preventDefault();
      if (mode !== 'info') {
        var cancelBtn = document.getElementById('sched-reminder-cancel');
        if (cancelBtn) cancelBtn.focus();
      }
    } else if (key === 'Back') {
      e.preventDefault();
      onCancel();
    }
  }, [onConfirm, onCancel, mode]));

  return (
    <div className="sched-reminder__overlay" onClick={onCancel}>
      <div className="sched-reminder__box" onClick={(e) => e.stopPropagation()}>
        {/* Channel icon + name */}
        <div className="sched-reminder__channel">
          {schedule.channelIcon && (
            <img
              className="sched-reminder__channel-icon"
              src={schedule.channelIcon}
              alt={schedule.channelName}
            />
          )}
          <span className="sched-reminder__channel-name">{schedule.channelName}</span>
        </div>

        {/* Program title + timing */}
        <div className="sched-reminder__program">
          <p className="sched-reminder__program-title">{schedule.title}</p>
          <p className="sched-reminder__timing">
            {formatTime(schedule.startMs)} &nbsp;·&nbsp; {timingLabel}
          </p>
        </div>

        {/* Question */}
        <p className="sched-reminder__question">
          {mode === 'info' ? t('scheduleReminder.gameStarting') : t('scheduleReminder.question')}
        </p>

        {/* Actions */}
        <div className="sched-reminder__actions">
          {mode === 'info' ? (
            <button
              ref={confirmRef}
              className="sched-reminder__btn sched-reminder__btn--yes"
              onClick={onCancel}
              autoFocus
            >
              {t('common.ok')}
            </button>
          ) : (
            <>
              <button
                ref={confirmRef}
                className="sched-reminder__btn sched-reminder__btn--yes"
                onClick={onConfirm}
                autoFocus
              >
                {t('scheduleReminder.watch')}
              </button>
              <button
                id="sched-reminder-cancel"
                className="sched-reminder__btn sched-reminder__btn--no"
                onClick={onCancel}
              >
                {t('common.cancel')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleReminderModal;
